import { useEffect, useMemo } from "react"
import { benefitApi } from "@/features/benefit-workspace/benefitApi"
import { useBenefitScopeArg } from "@/features/benefit-workspace/useBenefitChildReads"
import type { BenefitIntakeCatalog } from "@/features/benefit-workspace/benefitChildContracts"
import { useBenefitDraftState, useBenefitReviewActive, useBenefitSaving } from "@/features/benefit-workspace/benefitEditorContext"
import { IconAddressBook } from "@tabler/icons-react"
import { toast } from "sonner"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { EditableCardActions, Field, FieldGrid } from "./field"
import { useBenefitEdit } from "@/hooks/useBenefitEdit"
import { api } from "@/lib/api"
import { fmt, fmtPhone } from "@/lib/benefit-format"
import { formatPersonName } from "@/lib/person-identity"
import { intakeDetailsDraft, intakeDetailsRequest, intakeSecondaryOptions, intakeSecondaryAvailable } from "@/lib/intake-details"
import { changeIntakeParent, subtypesForParent, typedSelectionForId, type IntakeCatalogEntry, type IntakeCatalogParent, type IntakeSelectionDraft } from "@/lib/intake-catalog"
import { saveBenefitChange } from "@/lib/benefit-save"
import type { BenefitDetailDto } from "@/lib/benefit-types"

function historicalEntry(current: string, parent: IntakeCatalogParent | undefined): IntakeCatalogEntry[] {
  return current
    ? [{ id: `historical-${parent?.discriminator ?? "unknown"}-${current}`, parentDiscriminator: parent?.discriminator ?? "in-house", parentLabel: parent?.label ?? "Historical", code: "legacy", longLabel: current, isEnabled: false, version: 0 }]
    : []
}

function isKnownSelection(selection: IntakeSelectionDraft | null, catalog: BenefitIntakeCatalog | null, location: string) {
  if (!selection || selection.kind === "unset" || selection.kind === "legacy" || !catalog) return false
  const entry = catalog.entries.find(candidate => candidate.id === selection.subtypeId)
  return !!entry && entry.isEnabled && entry.version === selection.version && entry.longLabel === location && entry.parentDiscriminator === selection.kind
}

export function IntakeDetailsSection({ benefit, canEdit, canEditProgram: _canEditProgram = canEdit, onUpdated }: {
  benefit: BenefitDetailDto; canEdit: boolean; canEditProgram?: boolean; onUpdated: (updated: BenefitDetailDto) => void
}) {
  void _canEditProgram
  const [editing, setEditing, token] = useBenefitEdit(benefit.concurrencyToken, "intake")
  const [pending, setSaving] = useBenefitSaving()
  const reviewing = useBenefitReviewActive()
  const saving = pending || reviewing
  const [draft, setDraft] = useBenefitDraftState("intake", "draft", () => intakeDetailsDraft(benefit))
  const [secondaryOptions, setSecondaryOptions] = useBenefitDraftState("intake", "secondaryOptions", () => intakeSecondaryOptions(benefit))
  const secondaryAvailable = intakeSecondaryAvailable(benefit, draft.secondaryApplicantId)
  const secondaryChoices = secondaryOptions.filter((option, index) => secondaryOptions.findIndex(candidate => candidate.id === option.id) === index)
  const catalogRead = benefitApi.useGetBenefitIntakeCatalogQuery(useBenefitScopeArg())
  const catalog = !catalogRead.isError && !catalogRead.isFetching ? catalogRead.currentData ?? null : null
  const catalogError = catalogRead.isError
    ? "The intake catalog could not be loaded. Existing values remain available; reload before choosing a new subtype." : null
  useEffect(() => { if (!editing) setDraft(intakeDetailsDraft(benefit)) }, [benefit, editing])

  const secondary = benefit.personCollection.find(p => p.id === benefit.secondaryId)
  const parentByLabel = useMemo(() => new Map((catalog?.parents ?? []).map(parent => [parent.label, parent])), [catalog])
  const selectedParent = parentByLabel.get(draft.intakeType)
  const activeLocations = subtypesForParent(catalog?.entries ?? [], selectedParent)
  const currentKnown = isKnownSelection(draft.intakeSelection, catalog, draft.intakeLocation)
  const locationOptions = catalog
    ? [...activeLocations, ...(currentKnown ? [] : historicalEntry(draft.intakeLocation, selectedParent))]
    : historicalEntry(draft.intakeLocation, selectedParent)
  const configuredTypeOptions = catalog?.parents ?? []
  // While the authoritative catalog is unavailable, retain the current value as historical display
  // only. Never offer the old lookup list as new choices: it is not dependency-filtered or current.
  const typeOptions = draft.intakeType && !configuredTypeOptions.some(option => option.label === draft.intakeType)
    ? [{ discriminator: "historical", label: draft.intakeType }, ...configuredTypeOptions]
    : configuredTypeOptions
  const selectedLocationId = currentKnown
    ? draft.intakeSelection!.kind !== "unset" && draft.intakeSelection!.kind !== "legacy" ? draft.intakeSelection!.subtypeId : "__empty"
    : locationOptions.find(option => !option.isEnabled && option.longLabel === draft.intakeLocation)?.id ?? "__empty"

  const save = async () => {
    if (!editing || saving || !canEdit || !secondaryAvailable) return
    // Preserve an existing identity even if its catalog label/version has changed. The server
    // permits unchanged historical values, but validates every newly selected identity/version.
    const selection = draft.intakeSelection
    let request: ReturnType<typeof intakeDetailsRequest>
    try {
      request = intakeDetailsRequest(draft, token, selection)
    }
    catch (error) { toast.error(error instanceof Error ? error.message : "Check contact details."); return }
    setSaving(true)
    const result = await saveBenefitChange(benefit.id, onUpdated,
      () => api.patch<BenefitDetailDto>(`/api/benefitapplications/${benefit.id}/intake-details`, request),
      "Contact and intake details updated.")
    setSaving(false)
    if (result.saved || result.reloaded) setEditing(false)
  }

  return <Card>
    <CardHeader className="pb-3"><div className="flex items-center justify-between gap-3">
      <div className="flex items-center gap-2"><IconAddressBook className="size-5" /><div>
        <CardTitle className="text-base">Contact and Intake Details</CardTitle>
        <CardDescription>Application contacts and intake context; no shared person identity changes</CardDescription>
      </div></div>
      <EditableCardActions isEditing={editing} isSaving={saving} canEdit={canEdit} saveDisabled={!canEdit || !secondaryAvailable} restorePanelFocus
        onEdit={() => { if (!canEdit) return; setDraft(intakeDetailsDraft(benefit)); setSecondaryOptions(intakeSecondaryOptions(benefit)); setEditing(true) }} onCancel={() => setEditing(false)} onSave={save} />
    </div></CardHeader>
    <CardContent className="space-y-4">
      {editing ? <>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1.5"><Label htmlFor="intake-secondary">Secondary Applicant</Label>
            <Select value={draft.secondaryApplicantId || "none"} disabled={saving}
              onValueChange={v => setDraft(d => ({ ...d, secondaryApplicantId: v === "none" ? "" : v }))}>
              <SelectTrigger id="intake-secondary"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">None</SelectItem>
                {draft.secondaryApplicantId && !secondaryChoices.some(option => option.id === draft.secondaryApplicantId) && <SelectItem value={draft.secondaryApplicantId} disabled>Unavailable secondary applicant ({draft.secondaryApplicantId})</SelectItem>}
                {secondaryChoices.map(option => <SelectItem key={option.id} value={option.id} disabled={!intakeSecondaryAvailable(benefit, option.id)}>{option.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {!secondaryAvailable && <p role="status" className="text-xs text-destructive">The selected secondary applicant is no longer an eligible unique household member. Choose another member or None, or cancel and review the saved household.</p>}
          </div>
          {([ ["homePhone", "Home Phone", 25], ["messagePhone", "Message Phone", 25], ["contactEmail", "Contact Email", 100]] as const).map(([key, label, max]) => (
            <div className="space-y-1.5" key={key}><Label htmlFor={`intake-${key}`}>{label}</Label>
              <Input id={`intake-${key}`} type={key === "contactEmail" ? "email" : "text"} maxLength={max} value={draft[key]} disabled={saving}
                onChange={e => setDraft(d => ({ ...d, [key]: e.target.value }))} /></div>
          ))}
          <div className="space-y-1.5"><Label htmlFor="intake-intakeType">Intake Type</Label>
            <Select value={draft.intakeType || "__empty"} disabled={saving || !catalog} onValueChange={value => setDraft(d => changeIntakeParent(d, value === "__empty" ? "" : value))}>
              <SelectTrigger id="intake-intakeType"><SelectValue placeholder="Select intake type" /></SelectTrigger>
              <SelectContent><SelectItem value="__empty">Not recorded</SelectItem>
                {typeOptions.map(option => <SelectItem key={`type-${option.discriminator}`} value={option.label}>{option.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label htmlFor="intake-intakeLocation">Intake Location</Label>
            <Select value={draft.intakeLocation ? selectedLocationId : "__empty"} disabled={saving || !catalog || !draft.intakeType} onValueChange={value => {
              if (value === "__empty") setDraft(d => ({ ...d, intakeLocation: "", intakeSelection: null }))
              else {
                const entry = locationOptions.find(option => option.id === value)
                const parent = selectedParent
                const selected = entry && entry.isEnabled ? typedSelectionForId(catalog?.entries ?? [], parent, entry.id) : null
                setDraft(d => ({ ...d, intakeLocation: entry?.longLabel ?? "", intakeSelection: selected }))
              }
            }}>
              <SelectTrigger id="intake-intakeLocation"><SelectValue placeholder={draft.intakeType ? "Select intake location" : "Select a type first"} /></SelectTrigger>
              <SelectContent><SelectItem value="__empty">Not recorded</SelectItem>
                {locationOptions.map(option => <SelectItem key={`location-${option.id}`} value={option.id}>{option.longLabel}{!option.isEnabled ? " (historical)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {catalogError ? <p className="text-xs text-destructive">{catalogError}</p> : null}
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Changing the intake type clears its location. Staff, file number and audit timestamps are recorded by the system and cannot be overwritten here.</p>
      </> : <>
        <FieldGrid cols={3}>
          <Field label="Secondary Applicant" value={secondary ? formatPersonName(secondary) : "—"} />
          <Field label="Home Phone" value={fmtPhone(benefit.homePhone)} />
          <Field label="Message Phone" value={fmtPhone(benefit.messagePhone)} />
          <Field label="Contact Email" value={fmt(benefit.contactEmail)} />
          <Field label="Intake Type" value={fmt(benefit.intakeType)} />
          <Field label="Intake Location" value={fmt(benefit.intakeLocation)} />
        </FieldGrid>
      </>}
    </CardContent>
  </Card>
}
